/**
 * Deep link into the Permissions list, pre-filtered to one object.
 *
 * Built from `relationsCrudFilters` — the SAME state→filters mapping the
 * Permissions page itself uses — and serialized with refine's own
 * `stringifyTableParams`, so the link cannot drift from what that page parses
 * back on load.
 *
 * Why the object id goes in `q` rather than an `objectId` filter: `RelationsList`
 * seeds its toolbar from the restored URL and then, 250ms after mount, calls
 * `setFilters(relationsCrudFilters(state), 'replace')` (List.tsx). That replace
 * keeps ONLY the four toolbar fields, so any `objectId` passed in the URL is
 * silently dropped a quarter-second after arrival. `q` survives because the
 * toolbar owns it — and it is exact here: the hub matches `q` against a
 * substring of `object_id`/`subject_id` (routes/relations.ts
 * `buildSearchCondition`), and a uuid matches only its own row. `objectType`
 * ANDs with it, so the result is precisely that object's tuples.
 */
import { stringifyTableParams, unionFilters } from '@refinedev/core';
import { relationsCrudFilters } from './List';

export const PERMISSIONS_PATH = '/admin/permissions';

/**
 * `/admin/permissions?…` showing every grant on one object.
 *
 * Structural (`parent`) tuples stay hidden, matching the Permissions page's
 * own first-visit default — they are the object graph, not grants a person made.
 */
export function permissionsHrefForObject(objectType: string, objectId: string): string {
  const filters = relationsCrudFilters({
    objectType,
    search: objectId,
    relation: '',
    showStructural: false,
  });
  return `${PERMISSIONS_PATH}?${stringifyTableParams({
    filters: unionFilters([], filters),
    sorters: [],
  })}`;
}
